<template>
  <Teleport to="body">
  <div v-if="visible" class="salesperson-creation-modal-overlay">
    <div class="salesperson-creation-modal-card">
      <div class="salesperson-creation-modal-header">
        <span>Create New Sales Person</span>
        <div class="salesperson-creation-modal-icons">
          <i class="bi bi-x-circle" @click="handleCancel"></i>
        </div>
      </div>

      <div class="salesperson-creation-modal-body">
        <!-- Basic Information -->
        <div class="salesperson-creation-modal-section">
          <!-- Row 1: Sales Person Name and Email -->
          <div class="salesperson-creation-modal-form-row">
            <div class="salesperson-creation-modal-row">
              <label class="salesperson-creation-modal-label">Sales Person Name<span class="salesperson-creation-required">*</span></label>
              <input 
                class="salesperson-creation-modal-input" 
                type="text" 
                placeholder="Enter sales person name"
                v-model="form.salesPersonName"
                @input="clearError('salesPersonName')"
              />
              <div v-if="errors.salesPersonName" class="salesperson-creation-modal-error">{{ errors.salesPersonName }}</div>
            </div>

            <div class="salesperson-creation-modal-row">
              <label class="salesperson-creation-modal-label">Email<span class="salesperson-creation-required">*</span></label>
              <input 
                class="salesperson-creation-modal-input" 
                type="email" 
                placeholder="salesperson@company.com"
                v-model="form.email"
                @input="clearError('email')"
              />
              <div v-if="errors.email" class="salesperson-creation-modal-error">{{ errors.email }}</div>
            </div>
          </div>

          <!-- Row 2: Phone and Job Title -->
          <div class="salesperson-creation-modal-form-row">
            <div class="salesperson-creation-modal-row">
              <PhoneNumberInput 
                v-model="form.phone"
                :label="'Phone'"
                :required="true"
                label-class="salesperson-creation-modal-label"
                required-class="salesperson-creation-required"
                :placeholder="'Enter phone number'"
                :error-message="errors.phone"
                @input="handlePhoneInput"
              />
            </div>

            <div class="salesperson-creation-modal-row">
              <label class="salesperson-creation-modal-label">Job Title</label>
              <input 
                class="salesperson-creation-modal-input" 
                type="text" 
                placeholder="e.g., Sales Manager, Account Executive"
                v-model="form.jobTitle"
              />
            </div>
          </div>

          <!-- Row 3: Expense Account and Active Status -->
          <div class="salesperson-creation-modal-form-row">
            <div class="salesperson-creation-modal-row">
              <label class="salesperson-creation-modal-label">Expense Account</label>
              <select 
                class="salesperson-creation-modal-input"
                v-model="form.expenseAccountId"
                @change="handleExpenseAccountChange"
              >
                <option value="">Select an expense account</option>
                <option v-for="account in expenseAccounts" :key="account.id" :value="account.id">
                  {{ account.accountName }} ({{ account.accountNumber }}) - {{ account.accountType }}
                </option>
                <option value="__create_new__">Add New Account</option>
              </select>
            </div>

            <div class="salesperson-creation-modal-row">
              <label class="salesperson-creation-modal-label">Active Status</label>
              <div class="salesperson-creation-modal-checkbox-group" :class="{ 'checked': form.active }" @click="toggleActive">
                <input 
                  type="checkbox" 
                  id="active" 
                  v-model="form.active" 
                  @click.stop
                />
                <label for="active">Active</label>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="salesperson-creation-modal-actions">
        <button class="salesperson-creation-modal-ok" @click="handleOk" :disabled="loading">
          {{ loading ? 'Creating...' : 'Save' }}
        </button>
        <button class="salesperson-creation-modal-cancel" @click="handleCancel">Cancel</button>
      </div>
    </div>
  </div>

  <ErrorModal
    :show="showErrorModal"
    title="Validation Error"
    :message="errorMessage"
    @close="closeErrorModal"
  />

  <SuccessModal
    :show="showSuccessModal"
    title="Success"
    :message="successMessage"
    @close="closeSuccessModal"
  />
  </Teleport>
</template>

<script>
import '../styles/SalesPersonCreationDialog.css';
import ApiService from '@/services/api.service';
import logger from '@/utils/logger';
import ErrorModal from '@/components/ErrorModal.vue';
import SuccessModal from '@/components/SuccessModal.vue';
import PhoneNumberInput from '@/components/PhoneNumberInput.vue';

export default {
  name: 'SalesPersonCreationDialog',
  components: { ErrorModal, SuccessModal, PhoneNumberInput },
  emits: ['close', 'ok'],
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    companyId: {
      type: [String, Number],
      required: true
    }
  },
  data() {
    return {
      form: {
        salesPersonName: '',
        email: '',
        phone: '',
        jobTitle: '',
        expenseAccountId: '',
        active: true
      },
      expenseAccounts: [],
      errors: {},
      loading: false,
      showErrorModal: false,
      errorMessage: '',
      showSuccessModal: false,
      successMessage: '',
      createdSalesPerson: null
    };
  },
  methods: {
    clearError(field) {
      if (this.errors[field]) {
        delete this.errors[field];
      }
    },
    handlePhoneInput(value) {
      this.form.phone = value;
      this.clearError('phone');
    },
    resetForm() {
      this.form = {
        salesPersonName: '',
        email: '',
        phone: '',
        jobTitle: '',
        expenseAccountId: '',
        active: true
      };
      this.errors = {};
      this.loading = false;
      this.showErrorModal = false;
      this.errorMessage = '';
      this.showSuccessModal = false;
      this.successMessage = '';
      this.createdSalesPerson = null;
    },
    validate() {
      const errors = {};
      
      if (!this.form.salesPersonName.trim()) {
        errors.salesPersonName = 'Sales person name is required';
      }
      
      if (!this.form.email.trim()) {
        errors.email = 'Email is required';
      } else {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(this.form.email.trim())) {
          errors.email = 'Please enter a valid email address';
        }
      }
      
      if (!this.form.phone.trim()) {
        errors.phone = 'Phone number is required';
      }
      
      this.errors = errors;
      return Object.keys(errors).length === 0;
    },
    async handleOk() {
      if (!this.validate()) {
        // Show error modal with validation errors
        const errorMessages = Object.values(this.errors)
          .map(message => `• ${message}`)
          .join('\n');
        this.errorMessage = errorMessages;
        this.showErrorModal = true;
        return;
      }
      
      this.loading = true;
      try {
        const payload = {
          ...this.form,
          companyId: this.companyId
        };
        
        // Convert empty strings to null for optional fields
        if (payload.expenseAccountId === '') {
          payload.expenseAccountId = null;
        }
        
        const response = await ApiService.post(`/company/${this.companyId}/sales-persons`, payload);
        // Store the created sales person data
        this.createdSalesPerson = response.data;
        // Set success message and show modal
        this.successMessage = 'Sales person has been created successfully! Click OK to continue.';
        this.showSuccessModal = true;
        // Don't emit ok or reset form here - it will be done in closeSuccessModal
      } catch (error) {
        let errorMessage = 'Error creating sales person';
        
        if (error.response?.data?.error) {
          errorMessage = error.response.data.error;
        } else if (error.response?.data?.message) {
          errorMessage = error.response.data.message;
        } else if (error.message) {
          errorMessage = error.message;
        }
        
        this.errorMessage = errorMessage;
        this.showErrorModal = true;
      } finally {
        this.loading = false;
      }
    },
    handleCancel() {
      this.resetForm();
      this.$emit('close');
    },
    closeErrorModal() {
      this.showErrorModal = false;
      this.errorMessage = '';
    },
    closeSuccessModal() {
      this.showSuccessModal = false;
      this.successMessage = '';
      this.$emit('ok', this.createdSalesPerson);  // Emit ok event with created sales person data
      this.resetForm();
      this.$emit('close');  // Close the dialog
    },
    async fetchExpenseAccounts() {
      try {
        const response = await ApiService.get(`/company-account/company/${this.companyId}`);
        this.expenseAccounts = (response.data.accounts || []).filter(acc => 
          acc.accountType === 'Expense' || acc.accountType === 'Other Expense'
        );
      } catch (error) {
        logger.error('Error fetching expense accounts:', error);
        this.expenseAccounts = [];
      }
    },
    handleExpenseAccountChange(event) {
      const selected = event.target.value;
      if (selected === '__create_new__') {
        // Navigate to new account creation page
        this.$router.push(`/company/${this.companyId}/dashboard/new-account`);
      }
    },
    toggleActive() {
      this.form.active = !this.form.active;
    }
  },
  watch: {
    visible(newVal) {
      if (!newVal) {
        this.resetForm();
      } else {
        // Load data when dialog opens
        this.fetchExpenseAccounts();
      }
    }
  }
};
</script>
